import React, { useState } from 'react';
import { Button } from './ui/button';
import { useNotification } from '../contexts/NotificationContext';

interface PremiumFeatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
}

const PremiumFeatureModal: React.FC<PremiumFeatureModalProps> = ({ 
  isOpen, 
  onClose, 
  featureName = "AI generation" 
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(process.env.REACT_APP_WEBHOOK_WAITINGLIST_URL!, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_WEBHOOK_JWT_TOKEN}`
        },
        body: JSON.stringify({
          email: email.trim(),
          source: 'premium-feature-modal',
          timestamp: new Date().toISOString(),
          interested_feature: featureName
        })
      });

      if (response.ok) {
        showNotification('success', 'Welcome to the waiting list!', 'We\'ll reach out once our builder community is ready.');
        setEmail('');
        onClose();
      } else {
        throw new Error('Failed to join waiting list');
      }
    } catch (error) {
      console.error('Error joining builder community:', error);
      showNotification('error', 'Something went wrong', 'Please try again later or contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Build to learn</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 leading-relaxed text-base">
              AI usage can get <strong className="text-gray-900">costly at scale</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed text-base mt-4">
              But good news: we're building a community to help you <strong className="text-gray-900">build this yourself</strong> and power it with <strong className="text-gray-900">free, opensource models</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed text-base mt-4">
              Want in? <strong className="text-gray-900">Join the waiting list</strong> by sharing your email and we'll reach out when the community is live.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors text-gray-900 placeholder-gray-500"
                placeholder="your@email.com"
                required
              />
              <p className="text-sm text-gray-500 mt-2">
                We'll reach out once our builder community is ready
              </p>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Joining...
                  </div>
                ) : (
                  'Join Waiting List'
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4">
          <div className="flex items-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            No spam, just updates on the builder community
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumFeatureModal;